import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { switchMap } from 'rxjs';

import { RouteCodeEnum } from '@models/menu';
import { ModuleInstanceService } from '@module-instance/services';
import { TemplateTypeEnum } from '@shared/consts';
import { FileService, NotificationService, UserService } from '@shared/services';
import { DictionaryStore, UserStore } from '@signal-store/index';

import { KeyValueIsPrimary } from '@models/common';
import { AttachmentModel } from '@models/module/fields/main';
import { GetFolderParams, MyDriveService } from '@module-instance/my-drive/common';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { base64ToBlob, base64ToFile, getFileTypeFromContent, printBlob } from '@shared/utils';

export interface ActionDialogData {
  contractId: number;
  contractNo: string;
  moduleId: number;
  moduleName: string;
  moduleCode: RouteCodeEnum;
  entityInstances: EntityInstance[];
  actionType: 'download' | 'print' | 'email';
  isContractClosedOrArchived?: boolean;
}

export interface EntityInstance {
  entityInstanceId: number;
  documentNumber?: string;
  fileName?: string;
  isClosedState?: boolean;
  screenId: number;
}

@Component({
  standalone: true,
  imports: [
    FlexLayoutModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    TranslateModule,
  ],
  templateUrl: './actions-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionsDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<ActionsDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly userStore = inject(UserStore);
  private readonly dictionaryStore = inject(DictionaryStore);
  private readonly moduleInstanceService = inject(ModuleInstanceService);
  private readonly fileService = inject(FileService);
  private readonly myDriveService = inject(MyDriveService);
  private readonly notify = inject(NotificationService);
  private readonly userService = inject(UserService);
  readonly data = inject<ActionDialogData>(MAT_DIALOG_DATA);

  templates = signal<KeyValueIsPrimary<number>[]>([]);
  showTemplates = signal(true);
  selectedTemplateId = signal<number>(null);

  isPrint = computed(() => this.data.actionType === 'print');
  isEmail = computed(() => this.data.actionType === 'email');

  isBundleDocuments = signal(this.data.entityInstances.length >= 2);
  isIncludeActivity = signal(false);
  isIncludeDocumentAttachments = signal(false);
  isIncludeActivityAttachments = signal(false);

  isLoading = signal(false);

  ngOnInit(): void {
    this.init();
  }

  private init() {
    // Legacy: if multiple screens, check if they are the same.
    const uniqueScreenIds = [...new Set(this.data.entityInstances.map(x => x.screenId).filter(x => x))];
    const screenId = uniqueScreenIds.length === 1 ? uniqueScreenIds[0] : null;

    const allClosed = this.data.entityInstances.every(x => x.isClosedState);
    if (allClosed || this.data.isContractClosedOrArchived) {
      this.showTemplates.set(false);
    }

    this.dictionaryStore
      .getUserTemplates({
        contractId: this.data.contractId,
        moduleId: this.data.moduleId,
        screenId,
        typeName: TemplateTypeEnum.Printout,
      })
      .subscribe(templates => {
        if (!templates?.length) {
          this.notify.warning('pdfHandler.noTemplatesFound');
          this.dialogRef.close();
          return;
        }

        const defaultTemplate = templates.find(t => t.isPrimary) || templates[0];

        //  If multiple screens, restrict to default template only
        uniqueScreenIds.length > 1 ? this.templates.set([defaultTemplate]) : this.templates.set(templates);

        this.selectedTemplateId.set(defaultTemplate.key);
      });
  }

  onConfirm() {
    if (this.isLoading()) return;
    this.isLoading.set(true);

    const body = this.getRequestBody();
    const title = this.getFileName();

    if (this.data.actionType === 'email') {
      this.handleEmailAction(body, title);
    } else if (
      this.data.actionType === 'download' &&
      (this.isIncludeDocumentAttachments() || this.isIncludeActivityAttachments())
    ) {
      this.handleDownloadWithAttachments(body, title);
    } else {
      this.handleDirectDownload(body, title);
    }
  }

  private handleDownloadWithAttachments(
    body: { list: { title: string; entityInstanceId: number }[] | number[]; isPrint: boolean },
    title: string,
  ) {
    // Check size
    const limitSize = 104857600; // 100 MB
    this.moduleInstanceService
      .getAttachmentsSize({
        contractId: this.data.contractId,
        body: this.data.entityInstances.map(x => x.entityInstanceId),
      })
      .subscribe({
        next: size => {
          if (size > limitSize) {
            this.moduleInstanceService
              .backgroundDownloadInstances({
                contractId: this.data.contractId,
                body: this.data.entityInstances.map(x => x.entityInstanceId),
                title,
                templateId: this.selectedTemplateId() || undefined,
                isIncludeActivity: this.isIncludeActivity(),
                isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
                isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
              })
              .subscribe(() => {
                this.notify.warning('pdfHandler.downloadSizeLimit');
                this.dialogRef.close(true);
              });
          } else {
            this.handleDirectDownload(body, title);
          }
        },
        error: () => this.isLoading.set(false),
      });
  }

  private handleEmailAction(
    body: { list: { title: string; entityInstanceId: number }[] | number[]; isPrint: boolean },
    title: string,
  ) {
    const useBundleEndpoint = this.isBundleDocuments() && this.data.entityInstances.length > 1;

    if (useBundleEndpoint) {
      this.moduleInstanceService
        .getInstancesAsPdf({
          contractId: this.data.contractId,
          moduleId: this.data.moduleId,
          body,
          title,
          templateId: this.selectedTemplateId() || undefined,
          isIncludeActivity: this.isIncludeActivity(),
          isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
          isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
          isBundleDocuments: this.isBundleDocuments(),
        })
        .subscribe({
          next: response => {
            const data = Array.isArray(response) ? response : [response];
            if (!data.length) return;

            const fileType = getFileTypeFromContent(data[0]);
            if (!fileType) {
              this.notify.error('pdfHandler.fileTypeError');
              this.isLoading.set(false);
              return;
            }

            const baseFileName = title.replace(/\.(pdf|zip)$/i, '');
            const fileName = baseFileName + fileType.ext;
            const file = base64ToFile(data[0], fileName, fileType.mimeType);

            this.uploadAndOpenInboxDialog([file], fileType.mimeType);
          },
          error: () => this.isLoading.set(false),
        });
    } else {
      this.moduleInstanceService
        .getInstancesAsSeparatePdfs({
          contractId: this.data.contractId,
          moduleId: this.data.moduleId,
          body,
          templateId: this.selectedTemplateId() || undefined,
          isIncludeActivity: this.isIncludeActivity(),
          isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
          isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
          isBundleDocuments: this.data.entityInstances.length > 1 ? this.isBundleDocuments() : undefined,
        })
        .subscribe({
          next: response => {
            const data = Array.isArray(response) ? response : [response];
            if (!data.length) return;

            const firstFileType = getFileTypeFromContent(data[0]);
            if (!firstFileType) {
              this.notify.error('pdfHandler.fileTypeError');
              this.isLoading.set(false);
              return;
            }

            const files = data.map((base64Data, index) => {
              const fileType = getFileTypeFromContent(base64Data) || firstFileType;
              const fileName = this.getFileName(this.data.entityInstances[index], fileType.ext);
              return base64ToFile(base64Data, fileName, fileType.mimeType);
            });

            this.uploadAndOpenInboxDialog(files, firstFileType.mimeType);
          },
          error: () => this.isLoading.set(false),
        });
    }
  }

  private uploadAndOpenInboxDialog(files: File[], mimeType: string): void {
    const folderData: GetFolderParams = {
      id: 0,
      name: `${this.data.moduleName} - Shared`,
      parentId: -1,
    };

    this.myDriveService
      .getFolderId(folderData)
      .pipe(switchMap(folderId => this.myDriveService.uploadFiles(files, folderId, mimeType)))
      .subscribe({
        next: (uploadedAttachments: AttachmentModel[]) => {
          const attachmentsInMB = uploadedAttachments.map(a => ({
            ...a,
            length: (a.length || 0) / 1024 / 1024,
          }));
          this.openInboxDialog(attachmentsInMB);
        },
        error: () => this.isLoading.set(false),
      });
  }

  private openInboxDialog(attachments: AttachmentModel[]): void {
    this.dialogRef.close(false);

    import('@module-instance/inbox/inbox-dialog/inbox-dialog.component').then(m => {
      this.dialog.open(m.InboxDialogComponent, {
        data: {
          contractId: this.data.contractId,
          contractNo: this.data.contractNo,
          moduleId: this.data.moduleId,
          moduleName: this.data.moduleName,
          moduleCode: this.data.moduleCode,
          attachments,
          entityInstanceIds: this.data.entityInstances.map(x => x.entityInstanceId),
        },
        panelClass: ['cipo', 'cipo-dialog', 'classic', 'cipo--height--full'],
        ...this.userService.getResponsiveDialogSize().lg,
        disableClose: false,
      });
    });
  }

  private handleDirectDownload(
    body: { list: { title: string; entityInstanceId: number }[] | number[]; isPrint: boolean },
    title: string,
  ) {
    const useBundleEndpoint = this.isPrint() || (this.isBundleDocuments() && this.data.entityInstances.length > 1);

    if (useBundleEndpoint) {
      this.moduleInstanceService
        .getInstancesAsPdf({
          contractId: this.data.contractId,
          moduleId: this.data.moduleId,
          body,
          title,
          templateId: this.selectedTemplateId() || undefined,
          isIncludeActivity: this.isIncludeActivity(),
          isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
          isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
          isBundleDocuments: this.isPrint() ? true : this.isBundleDocuments(),
        })
        .subscribe({
          next: response => {
            const data = Array.isArray(response) ? response : [response];
            if (!data.length) return;

            const fileData = data[0];
            const { mimeType, ext } = getFileTypeFromContent(fileData) || this.getDefaultFileType();

            if (this.isPrint()) {
              const printMimeType = 'application/pdf';
              const blob = base64ToBlob(fileData, printMimeType);
              printBlob(blob);
            } else {
              this.fileService.downloadBase64File(
                fileData,
                ext === '.zip' ? title.replace(/\.pdf$/i, '') : title,
                mimeType,
              );
            }
            this.dialogRef.close(true);
          },
          error: () => this.isLoading.set(false),
        });
    } else {
      // Separate PDFs
      this.moduleInstanceService
        .getInstancesAsSeparatePdfs({
          contractId: this.data.contractId,
          moduleId: this.data.moduleId,
          body,
          templateId: this.selectedTemplateId() || undefined,
          isIncludeActivity: this.isIncludeActivity(),
          isIncludeDocumentAttachments: this.isIncludeDocumentAttachments(),
          isIncludeActivityAttachments: this.isIncludeActivityAttachments(),
          isBundleDocuments: this.data.entityInstances.length > 1 ? this.isBundleDocuments() : undefined,
        })
        .subscribe({
          next: response => {
            const data = Array.isArray(response) ? response : [response];
            const mimeType =
              this.isIncludeDocumentAttachments() || this.isIncludeActivityAttachments()
                ? 'application/zip'
                : 'application/pdf';
            const ext = this.isIncludeDocumentAttachments() || this.isIncludeActivityAttachments() ? '.zip' : '.pdf';

            const fileNames = this.data.entityInstances.map(instance => this.getFileName(instance, ext));

            this.fileService.downloadMultipleBase64Files(data, fileNames, mimeType);
            this.dialogRef.close(true);
          },
          error: () => this.isLoading.set(false),
        });
    }
  }

  private getRequestBody(): { list: { title: string; entityInstanceId: number }[] | number[]; isPrint: boolean } {
    const isBundle = this.isPrint() || this.isBundleDocuments();
    let list: { title: string; entityInstanceId: number }[] | number[];

    if (isBundle) {
      list = this.data.entityInstances.map(instance => instance.entityInstanceId);
    } else {
      list = this.data.entityInstances.map(instance => ({
        title: this.getFileName(instance, '.pdf'),
        entityInstanceId: instance.entityInstanceId,
      }));
    }

    return {
      list,
      isPrint: this.isPrint(),
    };
  }

  private getFileName(instance?: EntityInstance, ext: string = '.pdf') {
    let fileName = instance?.fileName;

    if (!fileName) {
      const documentNumber =
        instance?.documentNumber ||
        (this.data.entityInstances.length === 1 ? this.data.entityInstances[0].documentNumber : null);

      const moduleValue = documentNumber ? this.data.moduleName : this.data.moduleCode;

      const contractNo = this.data.moduleCode === RouteCodeEnum.Contracts ? null : this.data.contractNo;

      fileName = [moduleValue, documentNumber, contractNo].filter(x => x).join('-');

      if (!fileName) fileName = 'Documents';

      const timezone = this.userStore.userData()?.ianaTimeZoneId;
      const timestamp = moment.tz(new Date(), timezone).format('YYYY_MM_DD_HH_mm_ss');

      fileName += ` - ${timestamp}`;
    }

    return (fileName + ext).replace(/[<>:"\/\\|?*]+/g, '_');
  }

  private getDefaultFileType() {
    return {
      mimeType: this.isPrint()
        ? 'application/pdf'
        : this.isIncludeDocumentAttachments() || this.isIncludeActivityAttachments()
          ? 'application/zip'
          : 'application/pdf',
      ext: this.isIncludeDocumentAttachments() || this.isIncludeActivityAttachments() ? '.zip' : '.pdf',
    };
  }
}
